import { DOCUMENT } from "@angular/common";
import { inject, Injectable } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";

import {
  catchError,
  concatMap,
  finalize,
  map,
  Observable,
  of,
  ReplaySubject,
  Subject,
  switchMap,
  take,
  throwError,
  timeout,
} from "rxjs";
import { AuthSession } from "../../../models/api/auth";
import { isSupportedLang, SupportedLang } from "../../../models/ui/supportedLangs";
import { AuthService } from "../../api/auth/auth.service";
import { MessageService } from "@openng/optimus-ui/api";

const RECONCILIATION_TIMEOUT_MS = 5000;
const PENDING_LOCALE_KEY_PREFIX = "pendingPreferredLocale:";

interface LanguageMutation {
  id: number;
  language: SupportedLang;
  session: AuthSession;
  notifyOnFailure: boolean;
}

interface Reconciliation {
  session: AuthSession;
  completed: ReplaySubject<void>;
}

@Injectable({
  providedIn: "root",
})
export class PreferredLanguageService {
  private auth = inject(AuthService);
  private document = inject(DOCUMENT);
  private translate = inject(TranslateService);
  private message = inject(MessageService);
  private localStorage = this.document.defaultView?.localStorage;
  private mutations = new Subject<LanguageMutation>();
  private reconciliation: Reconciliation | undefined;
  private latestMutationId = 0;

  constructor() {
    this.mutations.pipe(concatMap((mutation) => this.executeMutation(mutation))).subscribe();
  }

  /** Reconciles the local cache with the authenticated user's backend preference. */
  public reconcile(): Observable<void> {
    const session = this.auth.getSession();
    if (!session) {
      return of(void 0);
    }

    if (this.reconciliation && this.sameSession(this.reconciliation.session, session)) {
      return this.reconciliation.completed.asObservable();
    }

    const completed = new ReplaySubject<void>(1);
    const reconciliation = { session, completed };
    this.reconciliation = reconciliation;
    const mutationIdAtStart = this.latestMutationId;

    this.auth
      .getAuthenticatedUser()
      .pipe(
        take(1),
        timeout(RECONCILIATION_TIMEOUT_MS),
        switchMap((response) => {
          if (!this.auth.isCurrentSession(session) || mutationIdAtStart !== this.latestMutationId) {
            return of(void 0);
          }

          const pendingLanguage = this.getPendingLanguage(session.userId);
          if (pendingLanguage) {
            if (response.preferredLocale === pendingLanguage) {
              this.clearPendingLanguage(session.userId);
              return this.apply(pendingLanguage);
            }

            this.apply(pendingLanguage).subscribe({ error: () => undefined });
            this.enqueueMutation(pendingLanguage, session, false);
            return of(void 0);
          }

          if (response.preferredLocale) {
            return this.apply(response.preferredLocale);
          }

          return of(void 0);
        }),
        catchError(() => of(void 0)),
        finalize(() => {
          completed.next();
          completed.complete();
          if (this.reconciliation === reconciliation) {
            this.reconciliation = undefined;
          }
        })
      )
      .subscribe();

    return completed.asObservable();
  }

  /** Applies a local-only selection or persists an authenticated profile selection. */
  public select(language: unknown, persistAuthenticated: boolean): void {
    if (!isSupportedLang(language)) {
      return;
    }

    this.apply(language).subscribe({ error: () => undefined });
    if (!persistAuthenticated) {
      return;
    }

    const session = this.auth.getSession();
    if (session) {
      this.setPendingLanguage(session.userId, language);
      this.enqueueMutation(language, session, true);
    }
  }

  private enqueueMutation(language: SupportedLang, session: AuthSession, notifyOnFailure: boolean): void {
    this.mutations.next({
      id: ++this.latestMutationId,
      language,
      session,
      notifyOnFailure,
    });
  }

  private executeMutation(mutation: LanguageMutation): Observable<void> {
    if (!this.auth.isCurrentSession(mutation.session)) {
      return of(void 0);
    }

    return this.auth.updatePreferredLocale(mutation.language).pipe(
      timeout(RECONCILIATION_TIMEOUT_MS),
      switchMap((response) => {
        if (!this.auth.isCurrentSession(mutation.session)) {
          return of(void 0);
        }

        if (response.preferredLocale !== mutation.language) {
          return throwError(() => new Error("Preferred locale update response does not match the requested language."));
        }

        if (
          mutation.id === this.latestMutationId &&
          this.getPendingLanguage(mutation.session.userId) === mutation.language
        ) {
          this.clearPendingLanguage(mutation.session.userId);
        }
        return of(void 0);
      }),
      catchError(() => {
        if (
          this.auth.isCurrentSession(mutation.session) &&
          mutation.id === this.latestMutationId &&
          mutation.notifyOnFailure
        ) {
          this.showSaveWarning();
        }
        return of(void 0);
      })
    );
  }

  private getPendingLanguage(userId: string): SupportedLang | undefined {
    const value = this.localStorage?.getItem(this.pendingLocaleKey(userId));
    if (!value) {
      return undefined;
    }

    if (isSupportedLang(value)) {
      return value;
    }

    this.localStorage?.removeItem(this.pendingLocaleKey(userId));
    return undefined;
  }

  private setPendingLanguage(userId: string, language: SupportedLang): void {
    this.localStorage?.setItem(this.pendingLocaleKey(userId), language);
  }

  private clearPendingLanguage(userId: string): void {
    this.localStorage?.removeItem(this.pendingLocaleKey(userId));
  }

  private pendingLocaleKey(userId: string): string {
    return `${PENDING_LOCALE_KEY_PREFIX}${userId}`;
  }

  private showSaveWarning(): void {
    this.message.add({
      key: "global",
      severity: "warn",
      summary: this.translate.instant("common.warning"),
      detail: this.translate.instant("profile_settings.language_save_warning"),
      life: 6000,
    });
  }

  private apply(language: SupportedLang): Observable<void> {
    this.localStorage?.setItem("lang", language);
    return this.translate.use(language).pipe(map(() => void 0));
  }

  private sameSession(a: AuthSession, b: AuthSession): boolean {
    return a.generation === b.generation && a.userId === b.userId;
  }
}
